import { DropResult } from '@hello-pangea/dnd';
import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CombinationFormFields } from '@/components/question/forms/combination/CombinationForm';
import {
  IsNewItemArgs,
  UseQuestionFieldArrayReturn,
} from '@/data/hook/question/answers/types';
import { useSpotlight } from '@/data/hook/shared/useSpotlight';
import { QuestionCreateDTO } from '@/models/Answer';
import { validateCombinationQuestions } from '@/utils/questions/validateAnswers';

type UseCombinationQuestionsReturn =
  UseQuestionFieldArrayReturn<QuestionCreateDTO> & {
    handleDragEnd: (result: DropResult) => void;
  };

export function useCombinationQuestions(): UseCombinationQuestionsReturn {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });
  const { t: tCombination } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.combinationForm',
  });

  const { control, getValues, setValue } =
    useFormContext<CombinationFormFields>();
  const {
    fieldState: { error },
  } = useController({ control, name: 'questions' });

  const { focus, clearIfMatches, isFocused } = useSpotlight();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'questions',
    keyName: 'fieldId',
    rules: {
      validate: (items = []) => validateCombinationQuestions(items, t),
    },
  });

  const handleAdd = () => {
    append({ text: tCombination('newItem') });
    focus(fields.length);
  };

  const handleUpdateText = (index: number, text: string) => {
    setValue(`questions.${index}.text`, text);
    setValue(`questions.${index}.mediaFile`, null);
    setValue(`questions.${index}.altText`, '');
    clearIfMatches(index);
  };

  const handleUpdateMedia = (
    index: number,
    mediaFile: File | string,
    altText?: string,
  ) => {
    setValue(`questions.${index}.mediaFile`, mediaFile);
    setValue(`questions.${index}.altText`, altText ?? null);
    setValue(`questions.${index}.text`, '');
    clearIfMatches(index);
  };

  const handleDelete = (index: number) => {
    const answers = getValues('answers') ?? [];
    const newTotal = answers.length - 1;

    answers.forEach((answer, i) => {
      if (i === index) return;
      const filtered = answer.validPositions.filter(p => p <= newTotal);
      if (filtered.length !== answer.validPositions.length) {
        setValue(`answers.${i}.validPositions`, filtered);
      }
    });

    remove(index);
    clearIfMatches(index);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    move(source.index, destination.index);
    clearIfMatches(source.index);
  };

  const isNewItem = ({ index }: IsNewItemArgs) => isFocused(index);

  return {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleDelete,
    handleDragEnd,
    isNewItem,
  };
}
